import { useReactiveVar } from '@apollo/client'
import { PlusIcon } from '@radix-ui/react-icons'
import { Dialog, Flex } from '@radix-ui/themes'
import { DataTestIDs } from 'shared/constants'
import {
  depositWalletAddressVar,
  isWalletDepositOpenVar
} from 'shared/store/wallet-deposit'
import { DialogShared } from 'shared/ui'
import { NeonButton } from 'shared/ui/neon'
import { useWalletBalances } from '../hooks/use-wallet-balances'
import { getFundingState } from '../utils/get-funding-state'
import { DepositAddress } from './deposit-address'
import { DepositNotes } from './deposit-notes'
import { DepositSteps } from './deposit-steps'
import { DialogDecorations } from './dialog-decorations'
import { NeonDialogHeader } from './neon-dialog-header'
import { ReadyToPlay } from './ready-to-play'
import styles from '../neon-wallets.module.scss'

/**
 * Deposit (FE-304): users fund their wallets themselves, SOL for the network
 * fees and USDT to play, from an exchange or another wallet. The "Ready to
 * play" goals fill in live as the balance subscription reports the deposit.
 * Mounted once in `App`; opened from Your wallets, Settings → Wallets and by
 * a game transaction refused for lack of SOL (`solana-bc/sol-fee.ts`).
 */
export const WalletDepositDialog: React.FC = () => {
  const address = useReactiveVar(depositWalletAddressVar)
  const balances = useWalletBalances()
  const balance = address ? balances[address] : undefined
  const { isReady } = getFundingState(balance)

  return (
    <DialogShared
      openStore={isWalletDepositOpenVar}
      width='84rem'
      className={styles.dialog}
    >
      <DialogDecorations />
      {address ?
        <Flex
          direction='column'
          gap='4'
          className={styles.dialogBody}
          data-testid={DataTestIDs.walletDepositDialog}
        >
          <NeonDialogHeader
            icon={<PlusIcon />}
            title={
              <>
                Top up your <b>wallet</b>
              </>
            }
            subtitle='Send SOL and USDT on Solana. Your balance updates here by itself.'
          />

          <div className={styles.depositGrid}>
            <DepositAddress address={address} />
            <Flex
              direction='column'
              gap='3'
            >
              <ReadyToPlay balance={balance} />
              <DepositSteps />
            </Flex>
          </div>

          <DepositNotes />

          <Dialog.Close>
            {isReady ?
              <NeonButton
                type='button'
                size='lg'
              >
                Let&apos;s play
              </NeonButton>
            : <button
                type='button'
                className={styles.secondaryButton}
                data-size='lg'
              >
                Done
              </button>
            }
          </Dialog.Close>
        </Flex>
      : null}
    </DialogShared>
  )
}
